/**
 * DisposalFacilitiesTab — notes for drivers on the disposal facilities your loads go to
 * (#45 PR 2, DEC-359 choice 2).
 *
 * Not the "Disposal Wells" tab: that one authorizes SWD wells. This lists the Paelode
 * catalog facilities a job is sent to. Without a search it shows the ones this company
 * deals with (its jobs went or were planned to go there, it has a price or an exclusion
 * there, or it already noted it); a search finds any active facility by name or by your
 * own name for it. Choosing one opens its notes. Notes are yours alone: other haulers
 * never see them. Editing needs the catalog permission (canManage; the API decides).
 */

import * as React from 'react';
import { PlaceNotesEditor } from '../place-notes/PlaceNotesEditor';
import { useNoteFacilities, type NoteFacility } from '../place-notes/place-notes';
import { useUrlDetail, useUrlParam } from '../../lib/url-state';

const subText: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };
const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-border-primary)',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
  background: 'none',
  border: 'none',
  color: 'var(--color-text-primary)',
};

export function DisposalFacilitiesTab({ canManage }: { canManage: boolean }): React.JSX.Element {
  // OPEN-191 — the search box and the open facility live in the URL (`?facility=<id>`), so
  // browser Back closes the notes and a refresh reopens them; the page cursor stays in memory.
  const [q, setQ] = useUrlParam('facilitiesQ', '', []);
  const [search, setSearch] = React.useState(q);
  const [cursors, setCursors] = React.useState<(string | null)[]>([null]);
  const [openId, setOpenId] = useUrlDetail('facility');
  // The facility last opened from the list, so its notes stay up when the list moves on (a
  // refetch, another page). A link to one not on the first page shows the list instead.
  const [opened, setOpened] = React.useState<NoteFacility | null>(null);
  const cursor = cursors[cursors.length - 1] ?? null;
  const { data, isLoading, isError } = useNoteFacilities(search, cursor);
  const open =
    openId === null
      ? null
      : (data?.facilities.find((f) => f.id === openId) ?? (opened?.id === openId ? opened : null));
  const setOpen = (f: NoteFacility | null): void => {
    setOpened(f);
    setOpenId(f?.id ?? null);
  };

  // Debounce the search box; a new search starts at the first page.
  React.useEffect(() => {
    const t = setTimeout(() => {
      setSearch(q);
      setCursors([null]);
    }, 300);
    return (): void => clearTimeout(t);
  }, [q]);

  if (open !== null) {
    return (
      <div style={{ maxWidth: '720px' }}>
        <button
          type="button"
          onClick={(): void => setOpen(null)}
          style={{ ...subText, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          ← All disposal facilities
        </button>
        <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '8px 0 2px' }}>{open.name}</h2>
        <p style={{ ...subText, marginBottom: '12px' }}>{placeLine(open)}</p>
        <PlaceNotesEditor
          kind="FACILITY"
          id={open.id}
          placeName={open.name}
          canManage={canManage && open.isActive}
        />
      </div>
    );
  }

  const list = data?.facilities ?? [];
  return (
    <div style={{ maxWidth: '720px' }}>
      <p style={{ ...subText, marginBottom: '10px' }}>
        Notes for drivers about the disposal facilities your loads go to: gate codes, which lane,
        hours. Only your company sees them. (Authorizing SWD wells is on the Disposal Wells tab.)
      </p>
      <input
        type="search"
        aria-label="Search disposal facilities"
        placeholder="Search any disposal facility by name"
        value={q}
        onChange={(e): void => setQ(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: '14px',
          borderRadius: '10px',
          border: '1px solid var(--color-border-secondary)',
          backgroundColor: 'var(--color-bg-tertiary)',
          color: 'var(--color-text-primary)',
          marginBottom: '10px',
        }}
      />
      {isLoading && <p style={subText}>Loading…</p>}
      {isError && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>
          Couldn&apos;t load the facilities.
        </p>
      )}
      {data !== undefined && list.length === 0 && (
        <p style={subText}>
          {search.trim() === ''
            ? 'No disposal facilities yet. Search to find one and add a note.'
            : 'No active facility matches that name.'}
        </p>
      )}
      <div role="list">
        {list.map((f) => (
          <button
            key={f.id}
            type="button"
            role="listitem"
            style={rowStyle}
            onClick={(): void => setOpen(f)}
          >
            <span>
              <span style={{ fontWeight: 600 }}>{f.name}</span>
              {!f.isActive && <span style={subText}> (inactive)</span>}
              <span style={{ ...subText, display: 'block' }}>{placeLine(f)}</span>
            </span>
            <span
              style={{
                ...subText,
                maxWidth: '50%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {f.note === null ? 'No notes' : f.note.notes}
            </span>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
        {cursors.length > 1 && (
          <button type="button" onClick={(): void => setCursors(cursors.slice(0, -1))}>
            Previous
          </button>
        )}
        {data?.nextCursor != null && (
          <button
            type="button"
            onClick={(): void => setCursors([...cursors, data.nextCursor ?? null])}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}

function placeLine(f: NoteFacility): string {
  return [f.city, f.county === null ? null : `${f.county} County`, f.state]
    .filter((x): x is string => x !== null && x !== '')
    .join(', ');
}
